import { yupResolver } from '@hookform/resolvers/yup';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Chip, { chipClasses } from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import {
  PrevailingWageFringeOvertimeTreatment,
  PrevailingWageOvertimeScope,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import FieldHelper from '~components/FormFields/v2/FieldHelper';
import NumberInput from '~components/FormFields/v2/NumberInput';
import SelectInput from '~components/FormFields/v2/SelectInput';
import TextInput from '~components/FormFields/v2/TextInput';
import { OvertimeRule } from '~hooks/useOvertimeRule/models';

import {
  OvertimeRuleDto,
  OvertimeRuleDtoAsyncError,
  overtimeRuleSchema,
  setOvertimeRuleSchemaDefaultValues,
} from './overtimeRuleSchema';

export type OvertimeRuleFormHandler = {
  onSubmit: () => Promise<OvertimeRuleDto | undefined>;
};

interface OvertimeRuleFormProps {
  asyncErrors?: OvertimeRuleDtoAsyncError[];
  overtimeRule?: OvertimeRule;
}

function getScopeOptions() {
  return [
    {
      value: PrevailingWageOvertimeScope.PW_HOURS_ONLY,
      label: t('administration.overtime_rule.scope_options.pw_hours_only'),
    },
    {
      value: PrevailingWageOvertimeScope.ALL_EMPLOYER_HOURS,
      label: t('administration.overtime_rule.scope_options.all_employer_hours'),
    },
  ];
}

function getFringeTreatmentOptions() {
  return [
    {
      value: PrevailingWageFringeOvertimeTreatment.STRAIGHT_TIME,
      label: t('administration.overtime_rule.fringe_treatment_options.straight_time'),
    },
    {
      value: PrevailingWageFringeOvertimeTreatment.PREMIUM,
      label: t('administration.overtime_rule.fringe_treatment_options.premium'),
    },
    {
      value: PrevailingWageFringeOvertimeTreatment.REDUCED,
      label: t('administration.overtime_rule.fringe_treatment_options.reduced'),
    },
  ];
}

const EXAMPLE_PRESET_KEYS = [
  'federal_davis_bacon',
  'ny_daily_reduced_fringe',
  'ny_daily_straight_time_fringe',
  'mn_daily_weekly_pw_hours',
  'ca_daily_weekly_double_time',
] as const;

const OvertimeRuleForm = forwardRef<OvertimeRuleFormHandler, OvertimeRuleFormProps>(
  function OvertimeRuleForm({ asyncErrors, overtimeRule }, ref) {
    const defaultValues = useMemo(
      () => setOvertimeRuleSchemaDefaultValues(overtimeRule),
      [overtimeRule],
    );

    const { handleSubmit, control, setError, clearErrors } = useForm<OvertimeRuleDto>({
      resolver: yupResolver(overtimeRuleSchema),
      mode: 'onSubmit',
      reValidateMode: 'onChange',
      defaultValues,
    });

    useImperativeHandle(
      ref,
      () => ({
        onSubmit() {
          return new Promise((resolve) => {
            handleSubmit(
              (data) => {
                resolve(data);
              },
              () => {
                resolve(undefined);
              },
            )();
          });
        },
      }),
      [handleSubmit],
    );

    useEffect(() => {
      if (asyncErrors) {
        for (const error of asyncErrors) {
          setError(error.field as keyof OvertimeRuleDto, {
            message: error.message,
          });
        }
      }

      return () => {
        clearErrors();
      };
    }, [asyncErrors, setError, clearErrors]);

    const hasAdvancedValues =
      (overtimeRule?.dailyThresholdHours !== null &&
        overtimeRule?.dailyThresholdHours !== undefined) ||
      (overtimeRule?.weeklyThresholdDays !== null &&
        overtimeRule?.weeklyThresholdDays !== undefined) ||
      (overtimeRule?.doubleTimeThresholdHours !== null &&
        overtimeRule?.doubleTimeThresholdHours !== undefined) ||
      (overtimeRule?.doubleTimeMultiplier !== null &&
        overtimeRule?.doubleTimeMultiplier !== undefined) ||
      (overtimeRule?.scope !== null &&
        overtimeRule?.scope !== undefined &&
        overtimeRule.scope !== PrevailingWageOvertimeScope.PW_HOURS_ONLY);

    return (
      <Box>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <TextInput
              control={control}
              isRequired
              label={t('form_fields.name')}
              name="name"
            />
            <FieldHelper text={t('administration.overtime_rule.form.helper.name')} />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <NumberInput
              control={control}
              isRequired
              label={t('form_fields.weekly_threshold_hours')}
              name="weekly_threshold_hours"
            />
            <FieldHelper
              text={t('administration.overtime_rule.form.helper.weekly_threshold_hours')}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <NumberInput
              control={control}
              isRequired
              label={t('form_fields.multiplier')}
              name="multiplier"
            />
            <FieldHelper
              text={t('administration.overtime_rule.form.helper.multiplier')}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <SelectInput
              control={control}
              isRequired
              label={t('form_fields.fringe_treatment')}
              name="fringe_treatment"
              options={getFringeTreatmentOptions()}
            />
            <FieldHelper
              text={t('administration.overtime_rule.form.helper.fringe_treatment')}
            />
          </Grid>
        </Grid>
        <Accordion
          defaultExpanded={hasAdvancedValues}
          disableGutters
          elevation={0}
          sx={{
            mt: 2,
            '&:before': { display: 'none' },
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                }}
              >
                {t('administration.overtime_rule.form.advanced_configuration')}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('administration.overtime_rule.form.advanced_configuration_hint')}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <NumberInput
                  control={control}
                  label={t('form_fields.daily_threshold_hours')}
                  name="daily_threshold_hours"
                />
                <FieldHelper
                  text={t(
                    'administration.overtime_rule.form.helper.daily_threshold_hours',
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <SelectInput
                  control={control}
                  isRequired
                  label={t('form_fields.scope')}
                  name="scope"
                  options={getScopeOptions()}
                />
                <FieldHelper text={t('administration.overtime_rule.form.helper.scope')} />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <NumberInput
                  control={control}
                  label={t('form_fields.weekly_threshold_days')}
                  name="weekly_threshold_days"
                />
                <FieldHelper
                  text={t(
                    'administration.overtime_rule.form.helper.weekly_threshold_days',
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <NumberInput
                  control={control}
                  label={t('form_fields.double_time_threshold_hours')}
                  name="double_time_threshold_hours"
                />
                <FieldHelper
                  text={t(
                    'administration.overtime_rule.form.helper.double_time_threshold_hours',
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <NumberInput
                  control={control}
                  label={t('form_fields.double_time_multiplier')}
                  name="double_time_multiplier"
                />
                <FieldHelper
                  text={t(
                    'administration.overtime_rule.form.helper.double_time_multiplier',
                  )}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
        {!overtimeRule && (
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                display: 'block',
                mb: 1,
              }}
            >
              {t('administration.overtime_rule.form.examples_title')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {EXAMPLE_PRESET_KEYS.map((key) => (
                <Chip
                  key={key}
                  label={
                    <Typography variant="caption">
                      <strong>
                        {t(`administration.overtime_rule.form.presets.${key}.label`)}
                      </strong>
                      {` — ${t(`administration.overtime_rule.form.presets.${key}.description`)}`}
                    </Typography>
                  }
                  size="small"
                  sx={{
                    height: 'auto',
                    [`& .${chipClasses.label}`]: { whiteSpace: 'normal', py: 0.5 },
                  }}
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  },
);

export default OvertimeRuleForm;
